import { ApplicationConfig, ErrorHandler, Injectable, Injector, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { usesBearerTokenAuth } from './core/auth-mode';
import { bootstrapCookieHandoff } from './core/auth/login-handoff-bootstrap';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withHashLocation, withPreloading, withNavigationErrorHandler, withRouterConfig, PreloadingStrategy, Route, TitleStrategy } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { isChunkLoadError, reloadOnceForChunkError } from './core/utils/chunk-reload';
import { isStampedVersion } from './core/utils/app-version';
import { isTauri } from './core/tauri-runtime';
import { isFlowWidgetSurface } from './core/desktop-flow';
import { WorkplaceTitleStrategy } from './core/strategies/workplace-title.strategy';
import { EMPTY, Observable, firstValueFrom, from } from 'rxjs';
import { routes } from './app.routes';
import { provideGraphQL, hydrateApolloCache } from './core/graphql.providers';
import { MapReuseStrategy } from './core/strategies/map-reuse.strategy';
import { CompositeRouteReuseStrategy } from './core/strategies/composite-route-reuse.strategy';
import { TeamsService } from './core/services/teams.service';
import { TeamsAuthService } from './core/services/teams-auth.service';
import { AnalyticsErrorHandler } from './core/services/analytics-error-handler';
import { CachePersistService } from './core/services/cache-persist.service';
import { LocaleService } from './core/services/locale.service';
import { PushRegistrationService } from './core/services/push-registration.service';
import { CallStore } from './features/calls/data/call.store';
import { CallLauncherService } from './features/calls/data/call-launcher.service';
import { PERSON_CALLS } from './shared/people/person-calls';
import { AppUpdateService } from './core/services/app-update.service';
import { provideAppTransloco } from './core/services/transloco.providers';
import { providePaperBridges } from './core/services/paper-bridge.providers';
// NB: import from `/core` (not the package root) — the root entry statically
// imports mapbox-gl + three, so a root import from app.config would hard-wire
// the whole map engine into the very first chunk.
import { isSlowConnection, RICOH_MAPS_ALIGNMENT_SOURCE, RICOH_MAPS_BUNDLE_SOURCE, RICOH_MAPS_AVAILABILITY, RICOH_MAPS_PLATFORM_PORT, RICOH_MAPS_CONFIG, RICOH_MAPS_STATUS_COLOURS } from '@ricoh-maps/angular/core';
import type { RicohMapsStatusColourSink } from '@ricoh-maps/angular/core';
import { applyEngineStatusColours } from './core/constants/availability';
import { LocationStateService } from './core/services/location-state.service';
import { MapsApiService } from './core/services/maps-api.service';
import { MapAvailabilityService } from './core/services/map-availability.service';
import { PlatformDataService } from './core/services/platform-data.service';
import { environment } from '../environments/environment';
import { regionGateActive } from './core/region';
// The TOKEN and its TYPE only — `hls-support` names no package and pulls in
// nothing. The library itself is reached through a dynamic import below.
import { HLS_POLYFILL_LOADER, HlsPolyfillLoader } from './shared/media/video-player/hls-support';
import { OrganisationVideosService } from './features/videos/organisation-videos.service';
import { rememberArcadeUrlParam } from './features/viewer/arcade/arcade-unlock';
import { isUnattendedSignageUrl } from './core/utils/unattended-route';
import { TranslocoService } from '@jsverse/transloco';
import { DEFAULT_COOKIE_POLICY_URL } from '@ricohproducts/consent';
import { provideConsent, provideConsentLanguageSync } from '@ricohproducts/consent/angular';

/**
 * Idle preloading — waits 3 seconds after boot before preloading any lazy
 * routes, then loads them ONE AT A TIME. The previous per-route `timer(3000)`
 * released every registered route simultaneously — a burst of ~30 chunk
 * fetches (several MB) that competed with the user's actual data requests
 * for the whole pipe on slow links. Serialising keeps at most one preload
 * in flight, so interactive queries always win the bandwidth race.
 *
 * Connection-aware: on slow-2g / 2g / 3g links, or when the user has Data
 * Saver enabled, we skip preloading entirely so we don't burn the user's
 * data plan on chunks they may never visit. (`isSlowConnection()` covers
 * 2g + Data Saver; 3g is checked here — background preloading is pure
 * bandwidth speculation, so it should be more conservative than the
 * feature-level degradations that use the shared helper.)
 */
// Must be @Injectable(): withPreloading() resolves the strategy through DI,
// and an undecorated class has no compiled factory in the production build —
// every live session threw NG0201 at bootstrap and preloading never ran.
@Injectable({ providedIn: 'root' })
class IdlePreloadStrategy implements PreloadingStrategy {
  /** Tail of the preload queue — each route's load chains onto the last. */
  private queue: Promise<unknown> = new Promise((resolve) =>
    setTimeout(resolve, 3000),
  );

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // ⚠ NEVER PRELOAD A PREVIEW HARNESS, AND THIS IS NOT ABOUT BANDWIDTH.
    //
    // Preloading a `loadComponent` route does not merely fetch its chunk — it
    // creates the route's standalone environment injector, and creating an
    // injector RUNS ITS ENVIRONMENT INITIALIZERS. Several harness routes use
    // `provideEnvironmentInitializer` to install a fake viewer so they can
    // draw controls that need actions nobody would really hold, and
    // `ViewerIdentityService` is `providedIn: 'root'`.
    //
    // So: sign in, the real viewer loads, and about three seconds later this
    // queue reached `/preview/hubs` and quietly replaced the signed-in person
    // with "You · you@ricoh.com" — an address in no database — on whatever
    // page happened to be open. Every permission gate in the app then answered
    // from that harness's two live-event actions. It looked like a flash of
    // your own name followed by somebody else's, on every page, every session,
    // and it read as "I don't have the roles for this".
    //
    // These routes are dev and QA surfaces reached by typing the URL. Nobody
    // is ever navigated to one, so there was nothing to make faster either.
    if (route.data?.['preview'] === true) return EMPTY;
    // Opted out: the Game Centre loads on navigation or on intent (a game card
    // hovered or focused, `features/games/games-prefetch.ts`), never at app start.
    if (route.data?.['preload'] === false) return EMPTY;

    // The desktop Flow widget window draws one capsule from events; the rest
    // of the app has no business loading in a 380px always-on-top window.
    if (isFlowWidgetSurface()) return EMPTY;

    if (isSlowConnection() || effectiveTypeIs3g()) return EMPTY;
    const next = this.queue
      .then(() => whileOutsideGames())
      .then(() => firstValueFrom(load(), { defaultValue: null }).catch(() => null));
    this.queue = next;
    return from(next);
  }
}

/**
 * Resolves once the page is outside the Game Centre. A game's first load
 * (its chunks, models, textures and sound) is the heaviest thing the app
 * does, so speculative route preloads wait rather than share the link with
 * it; they carry on when the player leaves. Hash routing: `#/games/…` and
 * `#/preview/games/…`.
 */
function whileOutsideGames(): Promise<void> {
  const inGames = (): boolean => /^#\/(preview\/)?games(\/|\?|$)/.test(location.hash);
  if (!inGames()) return Promise.resolve();
  return new Promise((resolve) => {
    const check = (): void => {
      if (inGames()) setTimeout(check, 2000);
      else resolve();
    };
    setTimeout(check, 2000);
  });
}

/** Network Information API 3g check (unknown/absent → NOT slow, matching
 *  the shared helper's Safari/Firefox stance). */
function effectiveTypeIs3g(): boolean {
  const conn = (navigator as Navigator & {
    connection?: { effectiveType?: string };
  }).connection;
  return conn?.effectiveType === '3g';
}

/**
 * PWA service worker (ngsw) enablement.
 *
 * Gated on a STAMPED build identity rather than `isDevMode()`/production for
 * the same reason as AppUpdateService.start(): `ngsw-worker.js` + `ngsw.json`
 * are build-time artefacts (`ng build` emits them; `ng serve` does not), and
 * every deploy pipeline stamps NG_APP_VERSION — including the dev-config
 * canary path where `isDevMode()` would be true. Local `ng serve` stays
 * unregistered (no 404 spam, no stale-cache surprises while developing).
 *
 * Disabled inside Tauri: the desktop shell serves the bundle locally and has
 * its own updater (UpdaterService) — a service worker there only adds a
 * second, competing cache layer.
 */
/**
 * Whether this page load shows the shared cookie consent banner as it boots.
 * Off with the CONSENT_ENABLED switch off (nothing starts it then), and on
 * two surfaces nobody can answer it from: the desktop Flow widget window (a
 * 380px capsule; the main window asks) and a signage PLAYER, which runs on an
 * unattended screen. Neither starts PostHog either (`App.ngOnInit`).
 */
function consentBannerStartsAtBoot(): boolean {
  if (!environment.CONSENT_ENABLED) return false;
  if (typeof window === 'undefined') return false;
  return !isFlowWidgetSurface() && !isUnattendedSignageUrl(window.location.hash);
}

function serviceWorkerEnabled(): boolean {
  return (
    !isTauri &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    isStampedVersion(environment.APP_VERSION)
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: AnalyticsErrorHandler },
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(IdlePreloadStrategy),
      withHashLocation(),
      // Angular 22's default is 'always'; pin today's behaviour until it is tested.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
      // Stale-deploy recovery: if a lazy route fails to load because its chunk
      // hash changed under a still-open tab, hard-reload once to pick up the
      // fresh index.html. Guarded against loops (see chunk-reload.ts).
      withNavigationErrorHandler((e) => {
        if (isChunkLoadError(e.error)) reloadOnceForChunkError();
      }),
    ),
    // PWA: Angular service worker — offline app shell, atomic versioned
    // deploys (no more stale-chunk half-loads), installability. Update
    // detection + the reload prompt stay owned by AppUpdateService, which
    // coordinates with SwUpdate when the worker is active.
    provideServiceWorker('ngsw-worker.js', {
      enabled: serviceWorkerEnabled(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: RouteReuseStrategy, useClass: CompositeRouteReuseStrategy },
    // HLS playback for browsers that have none of their own. The player
    // probes NATIVE support first and only asks this loader when the source
    // is an .m3u8 AND `canPlayType` came back empty — so Safari, which plays
    // HLS natively and better than any JS implementation, never fetches it,
    // and a plain MP4 never reaches either branch.
    //
    // The specifier is DYNAMIC on purpose: `hls-adapter` is the only module
    // that names `hls.js`, and reaching it through `import()` keeps the
    // library in its own lazy chunk. A static import here would put ~500 kB
    // into the initial bundle — which is already over its warning budget —
    // for every reader, including the ones who never open a video.
    //
    // Without this provider the token still resolves (to `async () => null`)
    // and an HLS source becomes a typed UNSUPPORTED error the player renders
    // as a sentence. That is a legitimate MP4-only configuration, not a
    // broken one; see `shared/media/video-player/hls-support.ts`.
    {
      provide: HLS_POLYFILL_LOADER,
      useValue: (() =>
        import('./shared/media/video-player/hls-adapter').then((m) =>
          m.createHlsAdapter(),
        )) satisfies HlsPolyfillLoader,
    },
    // Compose page title as `<route> — RICOH Workplace [· <Division>]`.
    // Per-route configs declare bare names; the strategy adds the suffix
    // and reactively updates when DivisionBrandingService.branding loads.
    { provide: TitleStrategy, useClass: WorkplaceTitleStrategy },
    OrganisationVideosService,
    // Bind the @ricoh-maps engine's "where is the user looking?" alignment
    // source to the app's root LocationStateService. The public viewer drives
    // ThreeJsService.load3dFurnitureLayer() directly (not renderBundle), so the
    // 3D furniture layer reads pickerAlignment from RICOH_MAPS_ALIGNMENT_SOURCE;
    // without this binding it falls back to the package's empty StandaloneLocationContext
    // and furniture renders un-aligned with the picker-transformed walls.
    { provide: RICOH_MAPS_ALIGNMENT_SOURCE, useExisting: LocationStateService },
    // Flow Live: Call and the live presence dot on people (⌘K, the hover card, the person panel). The people kit
    // reads the seam; with `flow.calls` off it offers nothing and asks nothing (0sm8.4).
    { provide: PERSON_CALLS, useExisting: CallLauncherService },
    // The engine's campus mode (the root CampusOrchestrator used by /map's
    // "View campus", AND the admin campus preview) fetches the campus + per-floor
    // bundles through RICOH_MAPS_BUNDLE_SOURCE. Bind it app-wide to EA's GraphQL
    // data service — without it, enterCampus()/loadCampusMap() hit the no-op
    // default and campus mode silently does nothing ("No buildings"). Safe for
    // single-building /map, which bypasses this port (passes bundles straight to
    // renderBundle()).
    { provide: RICOH_MAPS_BUNDLE_SOURCE, useExisting: MapsApiService },
    // Campus availability repaints: the engine's CampusOrchestrator paints each
    // building's desk/room colours + dots through these two ports. Without them
    // bound, campus buildings render uncoloured (the no-op defaults report
    // spaces=0 / colours=0 / dots=0). EA's MapAvailabilityService +
    // PlatformDataService implement the port contracts; binding them app-wide
    // (root) covers /map "View campus" AND the admin campus preview. Safe for
    // single-building /map: the orchestrator only drives these during campus
    // mode and saves/restores the active-location selector around every
    // per-building paint, so the live single-building store is never clobbered.
    { provide: RICOH_MAPS_AVAILABILITY, useExisting: MapAvailabilityService },
    { provide: RICOH_MAPS_PLATFORM_PORT, useExisting: PlatformDataService },
    // Engine config.
    //  - mapboxToken: the engine resolves its token from localStorage['mapbox_token']
    //    || config.mapboxToken. EA never wired the env token into the config, so a
    //    FRESH context (new browser, incognito, cleared storage, Playwright) had NO
    //    token and the viewer bailed with "Couldn't load the map" — only sessions
    //    that happened to have the localStorage copy worked. Wiring it here makes the
    //    build-time token an explicit, reliable fallback (localStorage still wins).
    //  - exposeMapForTesting: publishes window.__mapForTest (+ perf summary) for the
    //    Playwright map-QA harness — NON-production only. EA stopped providing
    //    RICOH_MAPS_CONFIG during the GAR-package migration, which silently disabled
    //    the campus e2e (it reads __mapForTest); this restores it.
    //  - parkedCarModel: the car parked on a booked bay (MapEngineService.setParkedCars).
    //    A generic hatchback (Archilogic's Golf VI, badges removed), hosted beside the
    //    map bundles in the public assets bucket. The name is versioned and cached
    //    immutably: a new model is a new file, never an overwrite.
    { provide: RICOH_MAPS_CONFIG, useValue: { mapboxToken: environment.MAPBOX_KEY, exposeMapForTesting: !environment.production, parkedCarModel: { url: 'https://storage.googleapis.com/ricoh-spaces-public-assets/models/cars/hatchback-v1.glb' } } },
    // Close the availability-colour seam. MapThemeService pushes the active
    // theme's status palette through RICOH_MAPS_STATUS_COLOURS whenever the
    // aesthetic or the accessible-colours toggle changes — but the token was
    // never bound here, so it hit its no-op default. Desk dots and space
    // fills kept reading the --avail-* CSS variables, which meant the
    // colour-blind-safe palette (and the high-contrast aesthetic) never
    // reached the map itself, only the surrounding chrome. The palette is
    // KEPT (applyEngineStatusColours), so a map re-initialising cannot put
    // the CSS greys back: the engine names a status from its own palette, and
    // #64748b in a colour map left a not-bookable room lidded and lit.
    {
      provide: RICOH_MAPS_STATUS_COLOURS,
      useValue: {
        apply: (colours: Partial<Record<string, string>>) =>
          applyEngineStatusColours(colours),
      } satisfies RicohMapsStatusColourSink,
    },
    ...provideGraphQL(),
    ...provideAppTransloco(),
    // The shared cookie consent banner (@ricohproducts/consent): one choice
    // across *.ricohworkplace.com, so a choice made at sign-in applies here
    // with no second banner. After Transloco, so the banner follows the app's
    // language. Behind CONSENT_ENABLED: off, it never starts.
    //
    // Storage: inside Teams and the Outlook add-in the app is framed, and the
    // package's own default (`isEmbedded`) keeps the choice in localStorage.
    // The Tauri desktop shell is a top-level window on `tauri.localhost`,
    // which no `.ricohworkplace.com` cookie reaches, so it is told to use
    // localStorage too. Left undefined on the web, so the default still holds.
    provideConsent({
      startOn: consentBannerStartsAtBoot() ? 'init' : 'manual',
      policies: {
        cookies: environment.COOKIE_POLICY_URL || DEFAULT_COOKIE_POLICY_URL,
        privacy: environment.PRIVACY_POLICY_URL || undefined,
      },
      useLocalStorage: isTauri || undefined,
    }),
    provideConsentLanguageSync(TranslocoService),
    // Back the Ricoh Paper library's i18n/locale/environment tokens with
    // the app's real services (Transloco, LocaleService, environment).
    ...providePaperBridges(),
    provideAppInitializer(async () => {
      // The hidden arcade's URL unlock must be read before the router rewrites the URL.
      rememberArcadeUrlParam();
      const teams = inject(TeamsService);
      const teamsAuth = inject(TeamsAuthService);
      const cachePersist = inject(CachePersistService);
      const locale = inject(LocaleService);
      const push = inject(PushRegistrationService);
      const appUpdate = inject(AppUpdateService);
      // Resolved after the awaits below, where inject() is no longer allowed.
      const injector = inject(Injector);

      // Begin polling for newer deploys (non-blocking). Detecting a fresh
      // build under an open tab lets the shell prompt a reliable reload
      // instead of users hitting a stale chunk map and hard-refreshing.
      appUpdate.start();

      // Hydrate Apollo's InMemoryCache from IndexedDB so cold reloads can
      // paint immediately from disk while a background fetch refreshes the
      // data. Race against a 200 ms timeout so a slow IndexedDB never blocks
      // boot — persistence still continues filling in afterwards in the
      // background.
      const hydrate = hydrateApolloCache(cachePersist);
      const timeout = new Promise<void>((r) => setTimeout(r, 200));
      try {
        await Promise.race([hydrate, timeout]);
      } catch (err) {
        console.warn('[App] Cache hydration race failed — continuing:', err);
      }

      // Data residency: on the desktop welcome gate (no region chosen yet) skip
      // network-touching init (Teams SSO, push) until the user picks a region.
      const regionGate = regionGateActive();

      // Outlook add-in task pane: office.js owns the host handshake there —
      // initialising teams-js in the same iframe would race it (Outlook web
      // answers the teams-js handshake for personal tabs, misclassifying the
      // pane as a tab and double-running SSO). AddinAuthService signs in.
      const addinSurface = window.location.hash.startsWith('#/addin');

      // A hosted meeting's join page, `/m/<code>` (P7 design 8.1): a door for
      // guests as well as employees. There, push auto-registration is
      // skipped, Teams starts only inside Teams, the cookie hand-off still
      // runs (so a signed-in employee is recognised) but its failure never
      // redirects, and "not signed in" means GUEST — the page reads the
      // viewer itself and nothing here sends anyone to sign in.
      const joinSurface = window.location.hash.startsWith('#/m/');
      const insideTeamsHost = window !== window.parent || !!window.opener;

      if (!regionGate && !addinSurface && (!joinSurface || insideTeamsHost)) {
        try {
          await teams.initialize();
          await teamsAuth.initialize();
        } catch (err) {
          console.warn('[App] Teams initialisation failed — continuing without Teams SSO:', err);
        }
      }

      // App construction starts viewer loads and background GraphQL reads.
      // Establish the first-party cookie before any of those can see an empty
      // jar and race the route guard into a sign-in redirect. Teams and add-in
      // handoffs keep their existing bearer bootstrap; the region gate stays
      // network-silent until the user chooses a region.
      if (!regionGate && !usesBearerTokenAuth(teams.isInTeams())) {
        if (joinSurface) {
          // Best effort: a guest has nothing to hand off, and a failure must
          // never turn the join page into a sign-in redirect.
          await bootstrapCookieHandoff().catch((err: unknown) => {
            console.warn('[App] Cookie hand-off failed on the join page — continuing as a guest:', err);
            return null;
          });
        } else {
          await bootstrapCookieHandoff();
        }
      }

      // Resolve the user's locale through the detection waterfall and
      // hand it to Transloco. Runs after Teams init so the Teams context
      // is available as a signal source.
      try {
        await locale.initialise();
      } catch (err) {
        console.warn('[App] Locale initialisation failed — defaulting to en-GB:', err);
      }

      // Silently re-register FCM web push when this browser already granted
      // permission and the user hasn't turned push off. Never prompts, never
      // blocks boot — fire-and-forget so a slow handshake can't stall startup.
      // Skipped behind the desktop region gate (no network until region chosen)
      // and in the Outlook add-in pane (push has no place in a task pane).
      if (!regionGate && !addinSurface) {
        if (!joinSurface) {
          void push.tryAutoRegister().catch((err) => {
            console.warn('[App] Push auto-registration failed — continuing:', err);
          });
        }

        // Flow Live calls: constructing the store starts CallRingService,
        // which subscribes to rings only once `flow.calls` is on and a viewer
        // is signed in. Neither loads livekit-client — that waits for a call.
        injector.get(CallStore);
      }
    }),
  ],
};
